import { Alert } from '@mui/material';
import { findIndex, isEmpty } from 'lodash';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { requisitesApi } from 'api';
import { DataGrid, RequisitesInfo } from 'components';
import { TIME_INTERVAL } from 'constants/time-interval.constants';
import { QueryKey } from 'enums';
import { WhiteTriangleContext, useWhiteTriangle } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites } from 'types';

import { TraderPayinOrdersPage } from '../OrdersPage';

export const TraderRequisitesWhiteTriangle: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.white_triangle',
  });

  const { setRequisitesIdFilter, filters } = useWhiteTriangle();

  const selectedRequisitesId = useMemo(
    () => filters?.requisitesId,
    [filters?.requisitesId],
  );

  const { data: requisites } = useQuery(
    QueryKey.RequisitesWhiteTriangle,
    requisitesApi.getAllMyWhiteTriangle,
    {
      keepPreviousData: true,
      refetchInterval: TIME_INTERVAL.FIFTEEN_SECONDS,
      refetchIntervalInBackground: true,
    },
  );

  const selectedRequisitesIndex = useMemo(
    () => findIndex(requisites, (item) => item.id === selectedRequisitesId),
    [requisites, selectedRequisitesId],
  );

  const columnsRequisites = useMemo(
    () => [
      {
        header: t('columns.requisite'),
        valueGetter: (item: Requisites) => (
          <div>
            <RequisitesInfo
              requisite={item}
              compact
              className="tw-break-words tw-w-full"
            />

            <div className="">
              <div className="tw-flex">
                <div className="tw-w-12 tw-font-bold">{t('limits.hour')}</div>
                <div className="tw-mx-2">{`${t('limits.count')}: ${
                  item.limits?.limitCountPerHour
                } / ${item.limitCountPerHour}`}</div>
                <div>{`${t('limits.sum')}: ${item.limits?.limitSumPerHour} / ${
                  item.limitSumPerHour
                }`}</div>
              </div>

              <div className="tw-flex">
                <div className="tw-w-12 tw-font-bold">{t('limits.day')}</div>
                <div className="tw-mx-2">{`${t('limits.count')}: ${
                  item.limits?.limitCountPerDay
                } / ${item.limitCountPerDay}`}</div>
                <div>{`${t('limits.sum')}: ${item.limits?.limitSumPerDay} / ${
                  item.limitSumPerDay
                }`}</div>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [t],
  );

  const handleRequisiteClick = useCallback(
    (item: Requisites) => {
      setRequisitesIdFilter(item.id);
    },
    [setRequisitesIdFilter],
  );

  const renderLeftContent = useCallback(
    () => (
      <div className="tw-mt-2 tw-mr-4 tw-max-h-[calc(100vh-24rem)] tw-overflow-y-auto tw-w-1/3 tw-max-w-1/3">
        {!selectedRequisitesId && (
          <div className="tw-mb-4">{t('select_requisite')}</div>
        )}
        <DataGrid
          tableStyle={{
            tableLayout: 'fixed',
          }}
          selectedRowIndex={selectedRequisitesIndex}
          onRowClick={handleRequisiteClick}
          columns={columnsRequisites}
          data={requisites}
        />
      </div>
    ),
    [
      columnsRequisites,
      handleRequisiteClick,
      requisites,
      selectedRequisitesId,
      selectedRequisitesIndex,
      t,
    ],
  );

  return (
    <Fragment>
      {isEmpty(requisites) && (
        <Alert severity="info" className="tw-w-full">
          {t('no_data')}
        </Alert>
      )}

      {!isEmpty(requisites) && (
        <WhiteTriangleContext.Provider
          value={{
            isWhiteTriangle: true,
            requisitesId: selectedRequisitesId || '',
            renderLeftContent,
          }}
        >
          <TraderPayinOrdersPage />
        </WhiteTriangleContext.Provider>
      )}
    </Fragment>
  );
};
